import { forwardRef, useEffect, useCallback, type HTMLAttributes } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ className, isOpen, onClose, title, description, size = 'md', children, ...props }, ref) => {
    // Handle escape key
    const handleEscape = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      },
      [onClose]
    );

    // Handle body scroll lock and escape key
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, handleEscape]);

    if (!isOpen) return null;

    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      // Telephone : 1rem de marge de chaque cote (le `p-4` du conteneur) au
      // lieu de 2rem, sinon on perd 64 px sur 375.
      full: 'max-w-[calc(100vw-4rem)] max-h-[calc(100vh-4rem)] max-sm:max-w-[calc(100vw-2rem)] max-sm:max-h-[calc(100vh-2rem)]',
    };

    const isLargeModal = ['2xl', '3xl', '4xl', '5xl', 'full'].includes(size);

    return (
      // Le conteneur plein ecran DEFILE : une modale plus haute que l'ecran
      // (telephone, petit portable) reste atteignable jusqu'a ses boutons,
      // alors que le `body` est bloque. Centrage par `m-auto` sur la modale
      // (et non `items-center`) : quand elle depasse, elle se cale en haut au
      // lieu d'etre coupee des deux cotes. Quand elle tient, rendu identique.
      <div className="fixed inset-0 z-[60] flex overflow-y-auto p-4" data-modal-scroll-container="">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-dark-900/40 backdrop-blur-[2px] transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          aria-describedby={description ? 'modal-description' : undefined}
          className={cn(
            'relative z-50 m-auto w-full rounded-xl bg-white shadow-xl flex flex-col overflow-visible',
            'animate-in fade-in-0 zoom-in-95',
            isLargeModal && 'max-h-[calc(100vh-2rem)]',
            sizeClasses[size],
            className
          )}
          {...props}
        >
          {/* Header */}
          {(title || description) && (
            <div className="flex flex-col space-y-1.5 p-6 pb-0 flex-shrink-0 max-sm:p-4 max-sm:pb-0 max-sm:pr-12">
              {title && (
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-dark-500"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p id="modal-description" className="text-sm text-neutral-500">
                  {description}
                </p>
              )}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className={cn(
              'absolute right-4 top-4 rounded-lg p-1 text-neutral-400 z-10',
              'hover:bg-neutral-100 hover:text-neutral-600',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'transition-colors'
            )}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className={cn('p-6 overflow-visible max-sm:p-4', isLargeModal && 'flex-1 min-h-0 flex flex-col')}>
            {children}
          </div>
        </div>
      </div>
    );
  }
);

Modal.displayName = 'Modal';

// ModalFooter
export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {}

const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
          className
        )}
        {...props}
      />
    );
  }
);

ModalFooter.displayName = 'ModalFooter';

export { Modal, ModalFooter };
